import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import LandingNav from "@/components/LandingNav";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { blogPosts } from "@/content/blogPosts";

const POSTS_PER_PAGE = 6;

const Blog = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const filteredPosts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return blogPosts;
    return blogPosts.filter(
      (post) =>
        post.title.toLowerCase().includes(query) ||
        post.description.toLowerCase().includes(query) ||
        post.tags.some((tag) => tag.toLowerCase().includes(query)),
    );
  }, [searchQuery]);

  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);
  const paginatedPosts = useMemo(() => {
    const start = (currentPage - 1) * POSTS_PER_PAGE;
    return filteredPosts.slice(start, start + POSTS_PER_PAGE);
  }, [filteredPosts, currentPage]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "TestAssets Blog",
    description: t(
      "blog.metaDescription",
      "Practical guides on test data management, test asset organization, QA productivity, and the tools that help engineering teams ship faster.",
    ),
    url: "https://testassets.io/blog",
    blogPost: blogPosts.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      description: p.description,
      datePublished: p.date,
      url: `https://testassets.io/blog/${p.slug}`,
    })),
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead
        title={t("blog.metaTitle", "Blog — Test Data Management & QA Productivity | TestAssets")}
        description={t(
          "blog.metaDescription",
          "Practical guides on test data management, test asset organization, QA productivity, and the tools that help engineering teams ship faster.",
        )}
        path="/blog"
        jsonLd={jsonLd}
        breadcrumbs={[
          { name: t("nav.start"), url: "/" },
          { name: t("nav.blog", "Blog"), url: "/blog" },
        ]}
      />
      <LandingNav />
      <main className="flex-1 container mx-auto px-6 py-16 max-w-5xl">
        <header className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            {t("blog.heroTitle", "The TestAssets Blog")}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t(
              "blog.heroSubtitle",
              "Test data management, QA productivity, and the small workflow tweaks that compound into hours saved every week.",
            )}
          </p>
        </header>

        <div className="relative max-w-md mx-auto mb-10">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder={t("blog.searchPlaceholder", "Search articles...")}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
            aria-label={t("blog.searchPlaceholder", "Search articles...")}
          />
        </div>

        {paginatedPosts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground">
              {t("blog.noResults", "No articles match your search.")}
            </p>
          </div>
        ) : (
          <>
            <div className="grid gap-6 md:grid-cols-2">
              {paginatedPosts.map((post) => (
                <Link key={post.slug} to={`/blog/${post.slug}`} className="group">
                  <Card className="h-full transition-colors hover:border-primary/50">
                    <CardHeader>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {post.tags.slice(0, 2).map((tag) => (
                          <Badge key={tag} variant="secondary">{tag}</Badge>
                        ))}
                      </div>
                      <CardTitle className="text-xl group-hover:text-primary transition-colors">
                        {post.title}
                      </CardTitle>
                      <CardDescription className="line-clamp-3">{post.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="text-xs text-muted-foreground">
                      <time dateTime={post.date}>
                        {new Date(post.date).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </time>
                      {" · "}
                      {t("blog.readMinutes", "{{count}} min read", { count: post.readMinutes })}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            {totalPages > 1 && (
              <nav
                role="navigation"
                aria-label={t("blog.paginationLabel", "Pagination")}
                className="mt-10 flex items-center justify-center gap-2"
              >
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  aria-label={t("blog.previousPage", "Previous page")}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  {t("blog.previous", "Previous")}
                </Button>

                <span className="text-sm text-muted-foreground px-2">
                  {t("blog.pageIndicator", "{{current}} / {{total}}", {
                    current: currentPage,
                    total: totalPages,
                  })}
                </span>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  aria-label={t("blog.nextPage", "Next page")}
                >
                  {t("blog.next", "Next")}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </nav>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Blog;